import dayjs from "dayjs";
import wretch from "wretch";
import { Duration } from "dayjs/plugin/duration";
import { match } from "ts-pattern";

import { Client, Member, Organization } from "./contexts/organization";
import { SegmentSize } from "./date";
import { Invitation } from "../features/user/invitation";
import { Lead } from "../features/user/complete-profile";
import {
  AccountingPointDTO,
  AccountingPointSchema,
  Aggregate,
  AreaDTO,
  AreaDTOSchema,
  AreaResponseSchemaWithEtag,
  AreaWriteRequestSchema,
  ConduitOperationDTO,
  DataAcquisitionDTO,
  DataAcquisitionRequestSchema,
  EnergyFlowDTO,
  EnergyFlowResponseSchema,
  EnergyPricesDTO,
  EnergyPricesSchema,
  EnergySystemDTO,
  EnergySystemDTOResponseSchema,
  HeatingValueWriteRequestSchema,
  LatestMeasurementsData,
  LatestMeasurementsDataResponseSchema,
  AreaConsumptionDTO,
  AreaEnergyConsumptionResponseSchema,
  MeasurementsData,
  MeasurementsDataResponseSchema,
  MeasurementValueWriteRequestSchema,
  MeasuringDeviceResponseSchema,
  MediumDTO,
  MediumDTOResponseSchemaWithEtag,
  MediumWriteRequestSchema,
  OperatingStatesResponseSchema,
  PatchEnergySystemSchema,
  ProsumerAspectDTO,
  ProsumerAspectResponseSchema,
  ProsumerAspectResponseSchemaWithEtag,
  ProsumerDTO,
  ProsumerInputDTO,
  ProsumerResponseSchema,
  ProsumerResponseSchemaWithEtag,
} from "./dto-schemas/energy-management";
import { MeasuringDevice } from "../features/ems/plan/domain/measuring-device";
import { addETag } from "./etag";
import { makeWretchInstance } from "./contexts/api";
import { EdgeDevice, makeTimeRange, Measurand, TimeRange } from "../features/ems/domain";
import { UserSearchResult } from "../features/admin/search";
import { LoadDuration, OperatingState } from "../features/ems/operating-states/domain";
import { ClientSchema, InvitationSchema, OrganizationSchema } from "./dto-schemas/identity";
import { Report } from "../features/ems/reports/reports";
import {
  ActionDTO,
  ActionPUTRequestSchema,
  ActionResponseSchema,
  UploadDocumentResponseSchema,
  UploadedDocument,
  UploadedDocumentResponseSchema,
} from "./dto-schemas/action-plan";
import { ActionStatus } from "../features/action-plan/domain/action-plan";

type WretchType = ReturnType<typeof makeWretchInstance>;

export function createApiClient(getAccessToken: (options: any) => Promise<any>, orgaId?: string) {
  const wretchInstance = makeWretchInstance(getAccessToken);
  return new ApiClient(wretchInstance, orgaId);
}

export class ApiClient {
  constructor(
    private wretchInstance: WretchType,
    private organizationId?: string
  ) {}

  // Energy system
  async getEnergySystem(): Promise<EnergySystemDTO> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/energy-system`
    );

    return EnergySystemDTOResponseSchema.parse(await response.json());
  }

  async updateConduits(operations: Array<ConduitOperationDTO>): Promise<void> {
    const patchOperations = operations.flatMap((o) =>
      match(o)
        .with({ operation: "add" }, (o) => ({ op: "add", path: "/conduits/-", value: o.conduit }))
        .with({ operation: "remove" }, (o) => [
          { op: "test", path: `/conduits/${o.index}`, value: o.conduit },
          { op: "remove", path: `/conduits/${o.index}` },
        ])
        .exhaustive()
    );

    await this.wretchInstance.patch(
      PatchEnergySystemSchema.parse(patchOperations),
      `/organization/${this.organizationId}/energy-system`
    );
  }

  // Measuring devices
  async getMeasuringDevices(timeout: number = 30000): Promise<Array<MeasuringDevice>> {
    const controller = new AbortController();

    setTimeout(() => {
      controller.abort();
    }, timeout);

    const response = await this.wretchInstance
      .signal(controller)
      .get(`/organization/${this.organizationId}/measuring-device`);

    return MeasuringDeviceResponseSchema.array().parse(await response.json());
  }

  // Prosumer
  async getProsumer(prosumerId: string, aspects?: Array<string>): Promise<ProsumerDTO> {
    const response = await this.wretchInstance
      .query({ aspect: aspects }, { omitUndefinedOrNullValues: true })
      .get(`/organization/${this.organizationId}/prosumer/${prosumerId}`);
    const etag = response.headers.get("ETag");

    if (!etag) {
      throw new Error("ETag header missing");
    }

    return addETag(ProsumerResponseSchema.parse(await response.json()), etag);
  }

  async getProsumers(options?: {
    orgaId?: string;
    aspects?: Array<string>;
  }): Promise<Array<ProsumerDTO>> {
    const response = await this.wretchInstance
      .query({ aspect: options?.aspects }, { omitUndefinedOrNullValues: true })
      .get(`/organization/${options?.orgaId ?? this.organizationId}/prosumer/`);

    return ProsumerResponseSchemaWithEtag.array().parse(await response.json());
  }

  async createProsumer(
    name: string,
    position: { top: number; left: number }
  ): Promise<{ id: string }> {
    const response = await this.wretchInstance.post(
      { name, position },
      `/organization/${this.organizationId}/prosumer/`
    );
    return {
      id: response.headers.get("location")!.split("/").pop()!,
    };
  }

  async updateProsumer(
    prosumerId: string,
    etag: string,
    fields: Partial<Omit<ProsumerDTO, "etag" | "id">>
  ) {
    const partialProsumerUpdateSchema = ProsumerResponseSchemaWithEtag.in
      .omit({ _links: true, id: true })
      .partial();

    await this.wretchInstance
      .headers({
        "If-Match": etag,
      })
      .patch(
        partialProsumerUpdateSchema.parse(fields),
        `/organization/${this.organizationId}/prosumer/${prosumerId}`
      );
  }

  async updateDataAcquisitions(
    prosumerId: string,
    prosumerETag: string,
    newDataAcquisitions: Array<DataAcquisitionDTO>
  ): Promise<void> {
    await this.wretchInstance
      .headers({
        "If-Match": prosumerETag,
      })
      .patch(
        {
          dataAcquisitions: DataAcquisitionRequestSchema.array().parse(newDataAcquisitions),
        },
        `/organization/${this.organizationId}/prosumer/${prosumerId}`
      );
  }

  async deleteProsumer(prosumerId: string, prosumerETag: string): Promise<void> {
    await this.wretchInstance
      .headers({
        "If-Match": prosumerETag,
      })
      .delete(`/organization/${this.organizationId}/prosumer/${prosumerId}`);
  }

  async addDocumentToProsumer(prosumerId: string, file: File) {
    const response = await this.wretchInstance.post(
      {
        fileName: file.name,
      },
      `/organization/${this.organizationId}/prosumer/${prosumerId}/document/`
    );
    const { uploadUrl } = UploadDocumentResponseSchema.parse(await response.json());
    const contentType = file.type || "application/octet-stream";
    await wretch().url(uploadUrl).content(contentType).put(file).res();
  }

  async getAllDocumentsOfProsumer(prosumerId: string): Promise<Array<UploadedDocument>> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/prosumer/${prosumerId}/document/`
    );
    return UploadedDocumentResponseSchema.array().parse(await response.json());
  }

  async deleteDocumentFromProsumer(prosumerId: string, fileName: string) {
    await this.wretchInstance.delete(
      `/organization/${this.organizationId}/prosumer/${prosumerId}/document/${fileName}`
    );
  }

  // Prosumer Aspects
  async getProsumerAspect(prosumerId: string, aspectId: string): Promise<ProsumerAspectDTO> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/prosumer/${prosumerId}/aspect/${aspectId}`
    );
    const etag = response.headers.get("ETag");
    if (!etag) {
      throw new Error("ETag header missing");
    }
    return addETag(ProsumerAspectResponseSchema.parse(await response.json()), etag);
  }

  async getProsumerAspects(prosumerId: string): Promise<Array<ProsumerAspectDTO>> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/prosumer/${prosumerId}/aspect/`
    );
    return ProsumerAspectResponseSchemaWithEtag.array().parse(await response.json());
  }

  async createProsumerAspect(
    prosumerId: string,
    aspectType: string,
    attributes: Record<string, string | number | boolean | Array<string>>
  ): Promise<void> {
    await this.wretchInstance.post(
      { ...attributes, aspectType },
      `/organization/${this.organizationId}/prosumer/${prosumerId}/aspect/`
    );
  }

  async updateProsumerAspect(
    prosumerId: string,
    aspectId: string,
    aspectETag: string,
    aspectType: string,
    attributes: Record<string, string | number | boolean | Array<string>>
  ): Promise<void> {
    await this.wretchInstance
      .headers({ "if-match": aspectETag })
      .put(
        { ...attributes, aspectType },
        `/organization/${this.organizationId}/prosumer/${prosumerId}/aspect/${aspectId}`
      );
  }

  async deleteProsumerAspect(
    prosumerId: string,
    aspectId: string,
    aspectETag: string
  ): Promise<void> {
    await this.wretchInstance
      .headers({ "if-match": aspectETag })
      .delete(`/organization/${this.organizationId}/prosumer/${prosumerId}/aspect/${aspectId}`);
  }

  // Accounting Point
  async getAccountingPoints(): Promise<Array<AccountingPointDTO>> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/accounting-point/`
    );
    return AccountingPointSchema.array().parse(await response.json());
  }

  async getAccountingPointData<Aggregates extends Aggregate>(
    accountingPointId: string,
    measurand: Measurand,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    tz: string,
    aggregates: Array<Aggregate>,
    segmentSize?: SegmentSize
  ): Promise<MeasurementsData<Aggregates>> {
    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());

    const response = await this.wretchInstance
      .query(
        {
          interval: makeTimeRange(start, end).isoformat(),
          tz,
          aggregate: aggregates,
          segmentSize,
        },
        { omitUndefinedOrNullValues: true }
      )
      .get(
        `/organization/${this.organizationId}` +
          `/accounting-point/${accountingPointId}` +
          `/data/${measurand.medium}/${symbolAsParam}`
      );
    return MeasurementsDataResponseSchema(aggregates, segmentSize != null).parse(
      await response.json()
    );
  }

  // Energy reports
  async getAllEnergyReports(): Promise<Array<Report>> {
    const response = await this.wretchInstance.get(`/organization/${this.organizationId}/report/`);
    return response.json();
  }

  async getEnergyReport(id: string): Promise<{ url: string }> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/report/${id}`
    );
    return response.json();
  }

  // Organization
  async getOrganizations(): Promise<Array<Organization>> {
    const response = await this.wretchInstance.get("/organization/");
    return OrganizationSchema.array().parse(await response.json());
  }

  async getOrganizationById(id: string): Promise<Organization> {
    const response = await this.wretchInstance.get(`/organization/${id}`);
    return OrganizationSchema.parse(await response.json());
  }

  async searchOrganizationByName(name: string): Promise<Array<Organization>> {
    const response = await this.wretchInstance.get(`/organization/?name=${name}`);
    return OrganizationSchema.array().parse(await response.json());
  }

  async createOrganization(name: string) {
    await this.wretchInstance.post({ name: name }, "/organization/");
  }

  async createEmptyOrganization(name: string) {
    await this.wretchInstance.post({ name: name, members: [] }, "/organization/");
  }

  async deleteOrganization(id: string) {
    await this.wretchInstance.delete(`/organization/${id}`);
  }

  async getOrganizationMembers(): Promise<Array<Member>> {
    const response = await this.wretchInstance.get(`/organization/${this.organizationId}/member`);
    return response.json();
  }

  async getOrganizationInvitations(): Promise<Array<Invitation>> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/invitation/`
    );
    return InvitationSchema.array().parse(await response.json());
  }

  async renameOrganization(newName: string) {
    await this.wretchInstance.patch({ name: newName }, `/organization/${this.organizationId}`);
  }

  async updateFiscalYearStartMonth(month: number | null) {
    await this.wretchInstance.patch(
      { fiscalYearStartMonth: month },
      `/organization/${this.organizationId}`
    );
  }

  // Invitations
  async invitePersonToOrganization(recipientEmail: string) {
    await this.wretchInstance.post(
      { recipientEmail },
      `/organization/${this.organizationId}/invitation/`
    );
  }

  async retractInvitationToOrganization(recipientEmail: string) {
    await this.wretchInstance.delete(
      `/organization/${this.organizationId}/invitation/${recipientEmail}`
    );
  }

  // User
  async searchUser(searchTerm: string): Promise<Array<UserSearchResult>> {
    const response = await this.wretchInstance.get(`/user/?search=${searchTerm}`);
    return response.json();
  }

  async getInvitations(): Promise<Array<Invitation>> {
    const response = await this.wretchInstance.get("/user/me/invitation/");
    return InvitationSchema.array().parse(await response.json());
  }

  async deleteInvitationToOrga(orgaId: string): Promise<void> {
    await this.wretchInstance.delete(`/user/me/invitation/${orgaId}`);
  }

  async sendVerificationEmail(originPath: string) {
    await this.wretchInstance.post({ originPath }, "/user/email-verification");
  }

  async renameUser(newName: string) {
    await this.wretchInstance.patch({ name: newName }, "/user/me/");
  }

  async acceptInvitation(orgaId: string) {
    await this.wretchInstance.patch({}, `/user/me/invitation/${orgaId}`);
  }

  // Client Applications
  async getOrganizationClients(): Promise<Array<Client>> {
    const response = await this.wretchInstance.get(`/organization/${this.organizationId}/client`);
    return ClientSchema.array()
      .parse(await response.json())
      .map(({ createdAt, ...rest }) => ({
        ...rest,
        createdAt: dayjs(createdAt),
      }));
  }

  async deleteClient(clientId: string): Promise<void> {
    await this.wretchInstance.delete(`/organization/${this.organizationId}/client/${clientId}`);
  }

  async createClient(name: string): Promise<string> {
    const response = await this.wretchInstance.post(
      { name },
      `/organization/${this.organizationId}/client`
    );
    const locationHeader = response.headers.get("location");
    const newId = locationHeader?.split("/").pop();
    if (newId) {
      return newId;
    } else {
      throw new Error("Location header missing from response");
    }
  }

  // Edge device
  async getEdgeDevices(): Promise<ReadonlyArray<Readonly<EdgeDevice>>> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/edge-device/`
    );
    return response.json();
  }

  async addEdgeDevice({ mission, type }: { mission: string; type: "agent1" | "agent2" }) {
    await this.wretchInstance.put(
      { type },
      `/organization/${this.organizationId}/edge-device/${mission}`
    );
  }

  // Compat
  async getLinkToAgent(organizationId?: string): Promise<{ location: string }> {
    const response = await this.wretchInstance.get(
      `/organization/${organizationId ?? this.organizationId}/compat/link-to-agent`
    );
    return response.json();
  }

  async getNewAgentPassword(): Promise<{ password: string }> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/compat/new-password`
    );
    return response.json();
  }

  // License
  async grantLicense(orgaId: string, licenseName: string): Promise<void> {
    await this.wretchInstance.put({}, `/organization/${orgaId}/license/${licenseName}`);
  }

  async revokeLicense(orgaId: string, licenseName: string): Promise<void> {
    await this.wretchInstance.delete(`/organization/${orgaId}/license/${licenseName}`);
  }

  // Measurements Data
  async getMeasurementsData<Aggregates extends Aggregate>(
    prosumerId: string,
    measurand: Measurand,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    tz: string,
    aggregates: Array<Aggregate>,
    segmentSize?: SegmentSize,
    orgaId?: string
  ): Promise<MeasurementsData<Aggregates>> {
    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());
    const organizationId = orgaId ?? this.organizationId;

    const response = await this.wretchInstance
      .query(
        {
          interval: makeTimeRange(start, end).isoformat(),
          tz,
          aggregate: aggregates,
          segmentSize,
        },
        { omitUndefinedOrNullValues: true }
      )
      .get(
        `/organization/${organizationId}` +
          `/prosumer/${prosumerId}` +
          `/data/${measurand.medium}/${symbolAsParam}`
      );
    return MeasurementsDataResponseSchema(aggregates, segmentSize != null).parse(
      await response.json()
    );
  }

  async getLatestMeasurementsData(
    prosumerId: string,
    measurand: Measurand
  ): Promise<LatestMeasurementsData> {
    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());

    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}` +
        `/prosumer/${prosumerId}` +
        `/data/${measurand.medium}/${symbolAsParam}/latest`
    );
    return LatestMeasurementsDataResponseSchema.parse(await response.json());
  }

  async addMeasurementValue(
    prosumerId: string,
    measurand: Measurand,
    timestamp: dayjs.Dayjs,
    value: number
  ): Promise<void> {
    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());

    await this.wretchInstance.put(
      MeasurementValueWriteRequestSchema.parse({ value }),
      `/organization/${this.organizationId}` +
        `/prosumer/${prosumerId}` +
        `/data/${measurand.medium}/${symbolAsParam}/${encodeURIComponent(timestamp.format())}`
    );
  }

  async importMeasurementsData(
    file: Blob,
    prosumerId: string,
    measurand: Measurand
  ): Promise<void> {
    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());
    const url =
      `/organization/${this.organizationId}` +
      `/prosumer/${prosumerId}` +
      `/data/${measurand.medium}/${symbolAsParam}/import`;
    const response = await this.wretchInstance.post(null, url);
    const { url: uploadUrl } = await response.json();
    await wretch().url(uploadUrl).headers({ "content-type": "text/csv" }).put(file).res();
  }

  async syncMeasurementsData(
    prosumerId: string,
    measurands: Array<Measurand>,
    timeRange: TimeRange,
    tz: string
  ): Promise<void> {
    await this.wretchInstance.post(
      {
        interval: timeRange.isoformat(),
        tz,
        measurands: measurands.map(({ medium, symbol }) => ({
          medium,
          symbol: { name: symbol.name, subscript: symbol.subscript },
        })),
      },
      `/organization/${this.organizationId}/prosumer/${prosumerId}/sync-data`
    );
  }

  async deleteMeasurementsData(
    prosumerId: string,
    measurand: Measurand,
    timeRange: TimeRange,
    tz: string
  ): Promise<void>;
  async deleteMeasurementsData(prosumerId: string, measurand: Measurand): Promise<void>;
  async deleteMeasurementsData(
    prosumerId: string,
    measurand: Measurand,
    timeRange?: TimeRange,
    tz?: string
  ): Promise<void> {
    function buildQueryParams(timeRange?: TimeRange, tz?: string) {
      if (!timeRange) return {};

      if (!tz) {
        throw new Error("Timezone must be provided");
      }

      return { interval: timeRange.isoformat(), tz };
    }

    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());

    await this.wretchInstance
      .query(buildQueryParams(timeRange, tz))
      .delete(
        `/organization/${this.organizationId}` +
          `/prosumer/${prosumerId}` +
          `/data/${measurand.medium}/${symbolAsParam}`
      );
  }

  // Analysis
  async getOperatingStates(
    prosumerId: string,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    tz: string
  ): Promise<{ operatingStates: Array<OperatingState>; loadDuration: LoadDuration }> {
    const response = await this.wretchInstance
      .query({
        interval: makeTimeRange(start, end).isoformat(),
        tz,
        prosumer: prosumerId,
      })
      .get(`/organization/${this.organizationId}/analysis/operating-states`);
    return OperatingStatesResponseSchema.parse(await response.json());
  }

  // Analytics
  async createLead(lead: Lead) {
    await this.wretchInstance.post(lead, "/analytics/lead");
  }

  // Energy Flow
  // NOTE: This is an experiment to return DTOs from the API client and create ViewModels later
  async getEnergyFlow(
    medium: string,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    tz: string
  ): Promise<EnergyFlowDTO> {
    const response = await this.wretchInstance
      .query({
        interval: makeTimeRange(start, end).isoformat(),
        tz,
      })
      .get(`/organization/${this.organizationId}/energy-flow/${medium}`);
    return EnergyFlowResponseSchema.parse(await response.json());
  }

  // Energy Prices
  async getEnergyPrices(
    timeRange: TimeRange,
    tz: string,
    biddingZone: string,
    resolution: Duration
  ): Promise<EnergyPricesDTO> {
    if (biddingZone != "DE_LU") {
      throw new Error("Only DE_LU is supported");
    }

    const response = await this.wretchInstance
      .query({
        interval: timeRange.isoformat(),
        tz,
        resolution: resolution.toISOString(),
      })
      .get(`/organization/${this.organizationId}/price/day-ahead`);
    return EnergyPricesSchema.parse(await response.json());
  }

  // Reduction
  async getAllActions() {
    const response = await this.wretchInstance.get(`/organization/${this.organizationId}/action`);
    const json = await response.json();
    return ActionResponseSchema.array().parse(json);
  }

  async createAction(status: ActionStatus) {
    const response = await this.wretchInstance.post(
      { status },
      `/organization/${this.organizationId}/action`
    );
    const actionId = response.headers.get("actionid");
    if (!actionId) {
      throw new Error("Missing actionId in response headers");
    }
    return {
      status: 201,
      actionId,
    };
  }

  async editAction(action: ActionDTO) {
    const parsedAction = ActionPUTRequestSchema.parse(action);
    await this.wretchInstance.put(
      parsedAction,
      `/organization/${this.organizationId}/action/${action.id}`
    );
    return {
      status: 200,
    };
  }

  async deleteAction(actionId: string) {
    await this.wretchInstance.delete(`/organization/${this.organizationId}/action/${actionId}`);
    return {
      status: 204,
    };
  }

  async getActionPreview(action: ActionDTO) {
    const parsedAction = ActionPUTRequestSchema.parse(action);
    const response = await this.wretchInstance.post(
      parsedAction,
      `/organization/${this.organizationId}/action/preview`
    );
    const json = await response.json();
    return ActionResponseSchema.parse(json);
  }

  async addDocumentToAction(actionId: string, file: File) {
    const response = await this.wretchInstance.post(
      {
        fileName: file.name,
      },
      `/organization/${this.organizationId}/action/${actionId}/document/`
    );
    const { uploadUrl } = UploadDocumentResponseSchema.parse(await response.json());
    const contentType = file.type || "application/octet-stream";
    await wretch().url(uploadUrl).content(contentType).put(file).res();
    return {
      status: 200,
    };
  }

  async getAllDocumentsOfAction(actionId: string): Promise<Array<UploadedDocument>> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/action/${actionId}/document/`
    );
    return UploadedDocumentResponseSchema.array().parse(await response.json());
  }

  async deleteDocumentFromAction(actionId: string, fileName: string) {
    await this.wretchInstance.delete(
      `/organization/${this.organizationId}/action/${actionId}/document/${fileName}`
    );
    return {
      status: 204,
    };
  }

  // Media
  async getMedia(): Promise<ReadonlyArray<MediumDTO>> {
    const response = await this.wretchInstance.get(`/organization/${this.organizationId}/medium`);
    return MediumDTOResponseSchemaWithEtag.array().parse(await response.json());
  }

  async createMedium(label: string, color: string): Promise<string> {
    const response = await this.wretchInstance.post(
      MediumWriteRequestSchema.parse({ label, color }),
      `/organization/${this.organizationId}/medium`
    );
    return response.headers.get("location")!.split("/").pop()!;
  }

  async updateMedium(mediumId: string, etag: string, label: string, color: string): Promise<void> {
    await this.wretchInstance
      .headers({
        "If-Match": etag,
      })
      .put(
        MediumWriteRequestSchema.parse({ label, color }),
        `/organization/${this.organizationId}/medium/${mediumId}`
      );
  }

  async addHeatingValue(
    mediumId: string,
    mediumETag: string,
    timestamp: dayjs.Dayjs,
    value: number
  ): Promise<void> {
    await this.wretchInstance
      .headers({ "If-Match": mediumETag })
      .put(
        HeatingValueWriteRequestSchema.parse({ value }),
        `/organization/${this.organizationId}/medium/${mediumId}/data/H_s/${encodeURIComponent(timestamp.format())}`
      );
  }

  async getMediumData(
    mediumId: string,
    measurand: Measurand,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    tz: string
  ): Promise<MeasurementsData<"raw">> {
    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());
    const response = await this.wretchInstance
      .query(
        { interval: makeTimeRange(start, end).isoformat(), tz },
        { omitUndefinedOrNullValues: true }
      )
      .get(`/organization/${this.organizationId}/medium/${mediumId}/data/${symbolAsParam}`);
    return MeasurementsDataResponseSchema(["raw"], false).parse(await response.json());
  }

  async deleteMediumData(
    mediumId: string,
    measurand: Measurand,
    timeRange: TimeRange,
    tz: string
  ): Promise<void>;
  async deleteMediumData(mediumId: string, measurand: Measurand): Promise<void>;
  async deleteMediumData(
    mediumId: string,
    measurand: Measurand,
    timeRange?: TimeRange,
    tz?: string
  ): Promise<void> {
    function buildQueryParams(timeRange?: TimeRange, tz?: string) {
      if (!timeRange) return {};

      if (!tz) {
        throw new Error("Timezone must be provided");
      }

      return { interval: timeRange.isoformat(), tz };
    }

    const symbolAsParam = encodeURIComponent(measurand.symbol.toString());

    await this.wretchInstance
      .query(buildQueryParams(timeRange, tz))
      .delete(`/organization/${this.organizationId}/medium/${mediumId}/data/${symbolAsParam}`);
  }

  // Area
  async getAreas(): Promise<ReadonlyArray<AreaDTO>> {
    const response = await this.wretchInstance.get(`/organization/${this.organizationId}/area/`);
    return AreaResponseSchemaWithEtag.array().parse(await response.json());
  }

  async getArea(areaId: string): Promise<AreaDTO> {
    const response = await this.wretchInstance.get(
      `/organization/${this.organizationId}/area/${areaId}`
    );
    const etag = response.headers.get("ETag");
    if (!etag) {
      throw new Error("ETag header missing");
    }
    return addETag(AreaDTOSchema.parse(await response.json()), etag);
  }

  async createArea(
    name: string,
    description: string,
    prosumerInputs: ReadonlyArray<ProsumerInputDTO> = []
  ): Promise<void> {
    await this.wretchInstance.post(
      AreaWriteRequestSchema.parse({ name, description, prosumerInputs }),
      `/organization/${this.organizationId}/area/`
    );
  }

  async updateArea(
    areaId: string,
    etag: string,
    name: string,
    description: string,
    prosumerInputs: ReadonlyArray<ProsumerInputDTO> = []
  ): Promise<void> {
    await this.wretchInstance
      .headers({
        "If-Match": etag,
      })
      .put(
        AreaWriteRequestSchema.parse({ name, description, prosumerInputs }),
        `/organization/${this.organizationId}/area/${areaId}`
      );
  }

  async deleteArea(areaId: string, etag: string): Promise<void> {
    await this.wretchInstance
      .headers({
        "If-Match": etag,
      })
      .delete(`/organization/${this.organizationId}/area/${areaId}`);
  }

  async getAreaEnergyConsumption(
    areaId: string,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    tz: string,
    segmentSize?: SegmentSize
  ): Promise<AreaConsumptionDTO> {
    const response = await this.wretchInstance
      .query(
        {
          interval: makeTimeRange(start, end).isoformat(),
          tz,
          segmentSize,
        },
        { omitUndefinedOrNullValues: true }
      )
      .get(`/organization/${this.organizationId}/area/${areaId}/energy-consumption`);
    return AreaEnergyConsumptionResponseSchema.parse(await response.json());
  }
}
